/**
 * Lossback Page (คืนยอดเสีย)
 * 
 * หน้าจัดการระบบคืนยอดเสียสำหรับสมาชิก
 * - สร้างรายการคืนยอดเสียใหม่
 * - ดูรายการที่สร้างแล้ว
 * - ดูรายละเอียดและกดคืนยอด
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  RefreshCcw,
  Calendar,
  Users,
  DollarSign,
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  History,
  Loader2,
  TrendingDown,
  Gamepad2,
  ChevronRight,
  Plus,
  Eye,
  Clock,
  Send,
  ChevronLeft,
  Percent,
  Banknote,
  Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth/auth-provider'
import {
  ReturnLoseService,
  type ReturnLose,
  type ReturnLoseLog,
  type ReturnLoseStats,
  type ReturnLoseLogStats,
  ReturnLoseStatus,
  ReturnStatus,
  GAME_GROUPS,
  type CreateReturnLoseData,
} from '@/domain/return-lose'
import { ThaiDatePicker } from '@/components/ui/date-time-picker'
import { toDateInputValue } from '@/lib/date-format'

// ============================================
// Helper Functions
// ============================================

function formatNumber(value: number, decimals: number = 2): string {
  return value.toLocaleString('th-TH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function getStatusBadge(status: ReturnLoseStatus) {
  switch (status) {
    case ReturnLoseStatus.APPROVED:
      return (
        <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 gap-1">
          <CheckCircle className="w-3 h-3" />
          สำเร็จ
        </Badge>
      )
    case ReturnLoseStatus.PENDING:
      return (
        <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 gap-1">
          <Clock className="w-3 h-3" />
          รอดำเนินการ
        </Badge>
      )
    case ReturnLoseStatus.REJECTED:
      return (
        <Badge className="bg-red-500/15 text-red-600 border-red-500/30 gap-1">
          <XCircle className="w-3 h-3" />
          ปฏิเสธ
        </Badge>
      )
    default:
      return <Badge variant="secondary">ไม่ทราบ</Badge>
  }
}

function getReturnStatusBadge(status: ReturnStatus) {
  switch (status) {
    case ReturnStatus.SUCCESS:
      return (
        <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 gap-1">
          <CheckCircle className="w-3 h-3" />
          สำเร็จ
        </Badge>
      )
    case ReturnStatus.WAITING:
      return (
        <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 gap-1">
          <Clock className="w-3 h-3" />
          รอดำเนินการ
        </Badge>
      )
    case ReturnStatus.FAILED:
      return (
        <Badge className="bg-red-500/15 text-red-600 border-red-500/30 gap-1">
          <XCircle className="w-3 h-3" />
          ไม่สำเร็จ
        </Badge>
      )
    default:
      return <Badge variant="secondary">ไม่ทราบ</Badge>
  }
}

// ============================================
// Stats Cards Component
// ============================================

function StatsCards({ stats, isLoading }: { stats: ReturnLoseStats | null; isLoading: boolean }) {
  const cards = [
    { label: 'รายการทั้งหมด', value: stats?.total || 0, icon: History, color: 'text-blue-600', bg: 'bg-blue-500/10' },
    { label: 'รอดำเนินการ', value: stats?.pending || 0, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-500/10' },
    { label: 'สำเร็จแล้ว', value: stats?.approved || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-500/10' },
    { label: 'ยอดคืนรวม', value: stats?.totalReturned || 0, icon: DollarSign, color: 'text-pink-600', bg: 'bg-pink-500/10', format: true },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <Card key={card.label} className="border-zinc-200/50 dark:border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn("p-2.5 rounded-xl", card.bg)}>
                <card.icon className={cn("w-5 h-5", card.color)} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{card.label}</p>
                {isLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-lg font-bold mt-0.5">
                    {card.format ? `฿${formatNumber(card.value, 0)}` : card.value.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ============================================
// Create Dialog Component
// ============================================

function CreateReturnLoseDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}) {
  const { sessionToken } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedGames, setSelectedGames] = useState<string[]>(['slot'])
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0])
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0])
  const [amountLose, setAmountLose] = useState(100)
  const [percentReturn, setPercentReturn] = useState(5)
  const [afterBonus, setAfterBonus] = useState(true)
  const [returnBonus, setReturnBonus] = useState(false)
  const [turnover, setTurnover] = useState(0)
  const [notifyMember, setNotifyMember] = useState(true)

  const toggleGame = (gameId: string) => {
    setSelectedGames(prev => 
      prev.includes(gameId) 
        ? prev.filter(g => g !== gameId)
        : [...prev, gameId]
    )
  }

  const handleSubmit = async () => {
    if (!sessionToken || selectedGames.length === 0) return

    setIsSubmitting(true)
    try {
      const data: CreateReturnLoseData = {
        game_group: selectedGames,
        start_date: new Date(startDate),
        end_date: new Date(endDate),
        amount_lose: amountLose,
        percent_return: percentReturn,
        after_bonus: afterBonus,
        return_bonus: returnBonus,
        return_bonus_turnover: turnover,
        notify_member: notifyMember,
      }

      await ReturnLoseService.createReturnLose(sessionToken, data)
      onSuccess()
      onClose()
    } catch (err: any) {
      console.error('Error creating return lose:', err)
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500/20 to-pink-500/5 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-pink-500" />
            </div>
            <div>
              <span className="text-lg">สร้างรายการคืนยอดเสีย</span>
              <DialogDescription className="mt-1">
                กำหนดเงื่อนไขการคืนยอดเสียให้สมาชิก
              </DialogDescription>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Game Selection */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Gamepad2 className="w-4 h-4 text-blue-500" />
              กลุ่มเกมที่คืนยอดเสีย <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {GAME_GROUPS.map((game) => (
                <label
                  key={game.value}
                  className={cn(
                    "flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer transition-all text-sm",
                    "hover:bg-muted/50 hover:border-primary",
                    selectedGames.includes(game.value) && "border-primary bg-primary/5"
                  )}
                >
                  <Checkbox
                    checked={selectedGames.includes(game.value)}
                    onCheckedChange={() => toggleGame(game.value)}
                  />
                  <span className="truncate">{game.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-500" />
                วันที่เริ่มต้น <span className="text-red-500">*</span>
              </Label>
              <ThaiDatePicker
                selected={startDate ? new Date(startDate) : null}
                onChange={(date) => setStartDate(date ? toDateInputValue(date) : '')}
                placeholder="เลือกวันที่เริ่มต้น"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-red-500" />
                วันที่สิ้นสุด <span className="text-red-500">*</span>
              </Label>
              <ThaiDatePicker
                selected={endDate ? new Date(endDate) : null}
                onChange={(date) => setEndDate(date ? toDateInputValue(date) : '')}
                placeholder="เลือกวันที่สิ้นสุด"
              />
            </div>
          </div>

          {/* Amount & Percent */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Banknote className="w-4 h-4 text-amber-500" />
                ยอดเสียขั้นต่ำ (บาท) <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={amountLose}
                onChange={(e) => setAmountLose(Number(e.target.value))}
                placeholder="100"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Percent className="w-4 h-4 text-pink-500" />
                เปอร์เซ็นต์คืน <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={percentReturn}
                onChange={(e) => setPercentReturn(Number(e.target.value))}
                placeholder="5"
              />
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <Label>หักโบนัสก่อนคืนยอดเสีย</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  หักยอดโบนัสที่ได้รับในวันนั้นออกก่อนคำนวณยอดเสีย
                </p>
              </div>
              <Switch checked={afterBonus} onCheckedChange={setAfterBonus} />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>คืนยอดเสียเป็นเงินโบนัส</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  คืนเป็นโบนัสแทนเงินสด (ต้องทำเทิร์น)
                </p>
              </div>
              <Switch checked={returnBonus} onCheckedChange={setReturnBonus} />
            </div>

            {returnBonus && (
              <div className="space-y-2">
                <Label>จำนวน Turn Over (X)</Label>
                <Input
                  type="number"
                  value={turnover}
                  onChange={(e) => setTurnover(Number(e.target.value))}
                  placeholder="0"
                />
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label className="flex items-center gap-2">
                  🔔 แจ้งเตือนสมาชิก
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  ส่งข้อความแจ้งเตือนไปยังกล่องข้อความของสมาชิกเมื่อคืนยอดสำเร็จ
                </p>
              </div>
              <Switch checked={notifyMember} onCheckedChange={setNotifyMember} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || selectedGames.length === 0}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            คำนวณยอดเสีย
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Detail Dialog Component
// ============================================

function ReturnLoseDetailDialog({
  returnLose,
  open,
  onClose,
  onProcess,
}: {
  returnLose: ReturnLose | null
  open: boolean
  onClose: () => void
  onProcess: () => void
}) {
  const { sessionToken } = useAuth()
  const [logs, setLogs] = useState<ReturnLoseLog[]>([])
  const [stats, setStats] = useState<ReturnLoseLogStats | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  // Load logs when dialog opens
  useEffect(() => {
    if (open && returnLose?.uuid && sessionToken) {
      loadData()
    }
  }, [open, returnLose?.uuid, sessionToken])

  const loadData = async () => {
    if (!returnLose?.uuid || !sessionToken) return

    setIsLoading(true)
    try {
      const [logsResult, statsResult] = await Promise.all([
        ReturnLoseService.findReturnLoseLogs(sessionToken, { uuid: returnLose.uuid, limit: 500 }),
        ReturnLoseService.getReturnLoseLogStats(sessionToken, returnLose.uuid),
      ])
      setLogs(logsResult.items)
      setStats(statsResult)
    } catch (err) {
      console.error('Error loading logs:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // ตาม Angular: setAllowReturnToMember(id, status)
  const handleToggleReturn = async (logId: string, currentValue: boolean) => {
    if (!sessionToken) return

    try {
      // Angular: setAllowReturnToMember(id, !status) - toggle status
      await ReturnLoseService.setAllowReturnToMember(sessionToken, logId, !currentValue)
      // Update local state
      setLogs(prev => prev.map(log => 
        log.objectId === logId ? { ...log, return_to_member: !currentValue } : log
      ))
    } catch (err) {
      console.error('Error toggling return:', err)
    }
  }

  // ตาม Angular: setReturnToMember(uuid)
  const handleProcess = async () => {
    if (!returnLose?.uuid || !sessionToken) return

    setIsProcessing(true)
    try {
      // Angular: setReturnToMember(returnLoseLogs[0].uuid)
      await ReturnLoseService.setReturnToMember(sessionToken, returnLose.uuid)
      setShowConfirm(false)
      onProcess()
      loadData()
    } catch (err: any) {
      console.error('Error processing:', err)
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsProcessing(false)
    }
  }

  const selectedCount = logs.filter(l => l.return_to_member && l.return_status === ReturnStatus.WAITING).length
  const totalSelectedAmount = logs
    .filter(l => l.return_to_member && l.return_status === ReturnStatus.WAITING)
    .reduce((sum, l) => sum + l.amount_return_count, 0)

  if (!returnLose) return null

  return (
    <>
      <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center">
                <Eye className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <span className="text-lg">รายละเอียดการคืนยอดเสีย</span>
                <DialogDescription className="mt-1">
                  {returnLose.uuid} • {returnLose.game_group?.join(', ')} • {returnLose.date_return}
                </DialogDescription>
              </div>
            </DialogTitle>
          </DialogHeader>

          {/* Stats Summary */}
          <div className="px-6 py-4 bg-muted/30 border-b">
            <div className="grid grid-cols-5 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats?.total || 0}</p>
                <p className="text-xs text-muted-foreground">สมาชิกทั้งหมด</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats?.waiting || 0}</p>
                <p className="text-xs text-muted-foreground">รอดำเนินการ</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{stats?.success || 0}</p>
                <p className="text-xs text-muted-foreground">สำเร็จ</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{stats?.failed || 0}</p>
                <p className="text-xs text-muted-foreground">ไม่สำเร็จ</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-pink-600">฿{formatNumber(stats?.totalAmount || 0, 0)}</p>
                <p className="text-xs text-muted-foreground">ยอดคืนรวม</p>
              </div>
            </div>
          </div>

          {/* Table */}
          <ScrollArea className="flex-1">
            <div className="px-6 py-4">
              {isLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>ไม่พบข้อมูลสมาชิก</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>สมาชิก</TableHead>
                      <TableHead className="text-right">ยอดแพ้/ชนะ</TableHead>
                      <TableHead className="text-right">โบนัส</TableHead>
                      <TableHead className="text-right">หลังหักโบนัส</TableHead>
                      <TableHead className="text-right">ยอดคืน</TableHead>
                      <TableHead className="text-center">คืนยอด</TableHead>
                      <TableHead className="text-center">สถานะ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, index) => (
                      <TableRow key={log.objectId}>
                        <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{log.member_id?.agent_username || '-'}</p>
                            <p className="text-xs text-muted-foreground">
                              {log.member_id?.firstname} {log.member_id?.lastname}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600">
                          {formatNumber(log.amount_count)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-blue-600">
                          {formatNumber(log.amount_bonus)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-amber-600">
                          {formatNumber(log.amount_after_bonus_count)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-600 font-bold">
                          {formatNumber(log.amount_return_count)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={log.return_to_member}
                            onCheckedChange={() => handleToggleReturn(log.objectId, log.return_to_member)}
                            disabled={log.return_status !== ReturnStatus.WAITING}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          {getReturnStatusBadge(log.return_status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-muted/30 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              เลือกคืน <strong className="text-foreground">{selectedCount}</strong> ราย •
              ยอดรวม <strong className="text-emerald-600">฿{formatNumber(totalSelectedAmount, 0)}</strong>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                ปิด
              </Button>
              {stats && stats.waiting > 0 && (
                <Button
                  onClick={() => setShowConfirm(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={selectedCount === 0}
                >
                  <Send className="w-4 h-4 mr-2" />
                  คืนยอดเสีย ({selectedCount} ราย)
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              ยืนยันการคืนยอดเสีย
            </AlertDialogTitle>
            <AlertDialogDescription>
              คุณต้องการคืนยอดเสียให้สมาชิก <strong>{selectedCount}</strong> ราย
              รวมเป็นเงิน <strong className="text-emerald-600">฿{formatNumber(totalSelectedAmount, 0)}</strong> หรือไม่?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleProcess}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={isProcessing}
            >
              {isProcessing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              ยืนยันคืนยอด
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ============================================
// Main Component
// ============================================

export function LossbackPage() {
  const { sessionToken } = useAuth()
  
  // ReturnLose state
  const [returnLoseList, setReturnLoseList] = useState<ReturnLose[]>([])
  const [stats, setStats] = useState<ReturnLoseStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [total, setTotal] = useState(0)
  
  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [selectedReturnLose, setSelectedReturnLose] = useState<ReturnLose | null>(null)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  
  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<ReturnLose | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Load data
  const loadData = useCallback(async () => {
    if (!sessionToken) return

    setIsLoading(true)
    try {
      const [listResult, statsResult] = await Promise.all([
        ReturnLoseService.findReturnLose(sessionToken, { page, limit, query: searchQuery || undefined }),
        ReturnLoseService.getReturnLoseStats(sessionToken),
      ])
      setReturnLoseList(listResult.items)
      setTotal(listResult.total)
      setStats(statsResult)
    } catch (err) {
      console.error('Error loading data:', err)
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken, page, limit, searchQuery])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleViewDetail = (item: ReturnLose) => {
    setSelectedReturnLose(item)
    setShowDetailDialog(true)
  }

  const handleCreateSuccess = () => {
    loadData()
  }

  const handleProcessSuccess = () => {
    loadData()
  }
  
  // Handle Delete
  const handleDelete = async () => {
    if (!deleteTarget?.objectId || !sessionToken) return
    
    setIsDeleting(true)
    try {
      await ReturnLoseService.deleteReturnLose(sessionToken, deleteTarget.objectId)
      setDeleteTarget(null)
      loadData()
    } catch (err: any) {
      console.error('Error deleting:', err)
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsDeleting(false)
    }
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <TrendingDown className="w-6 h-6 text-pink-500" />
            คืนยอดเสีย
          </h1>
          <p className="text-sm text-muted-foreground">
            จัดการระบบคืนยอดเสียให้สมาชิก • {total.toLocaleString()} รายการ
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={loadData} 
            disabled={isLoading}
          >
            <RefreshCcw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button className="gap-2" onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">สร้างรายการใหม่</span>
            <span className="sm:hidden">สร้าง</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards stats={stats} isLoading={isLoading} />

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="ค้นหา UUID..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(1) }}
          className="pl-10"
        />
      </div>

      {/* Table - ReturnLose */}
      <Card className="border-zinc-200/50 dark:border-zinc-800">
          <CardContent className="p-0">
            {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : returnLoseList.length === 0 ? (
            <div className="p-12 text-center">
              <TrendingDown className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-sm text-muted-foreground">ไม่พบรายการคืนยอดเสีย</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>กลุ่มเกม</TableHead>
                      <TableHead>วันที่คืน</TableHead>
                      <TableHead className="text-right">ยอดเสียขั้นต่ำ</TableHead>
                      <TableHead className="text-right">เปอร์เซ็นต์</TableHead>
                      <TableHead className="text-right">สมาชิก</TableHead>
                      <TableHead className="text-right">ยอดคืน</TableHead>
                      <TableHead className="text-center">สำเร็จ/ไม่สำเร็จ</TableHead>
                      <TableHead className="text-center">สถานะ</TableHead>
                      <TableHead>โดย</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {returnLoseList.map((item, index) => (
                      <TableRow key={item.objectId} className="hover:bg-muted/50">
                        <TableCell className="text-muted-foreground text-sm">
                          {(page - 1) * limit + index + 1}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {item.game_group?.slice(0, 3).map((game) => (
                              <Badge key={game} variant="secondary" className="text-xs">
                                {game}
                              </Badge>
                            ))}
                            {item.game_group && item.game_group.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{item.game_group.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {item.date_return || formatDate(item.start_date)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-600 font-bold">
                          {formatNumber(item.amount_lose, 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-blue-600 font-bold">
                          {item.percent_return}%
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          <span className="text-red-600 font-bold">{item.member_count}</span>
                          <span className="text-muted-foreground text-xs ml-1">ราย</span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-pink-600 font-bold">
                          {formatNumber(item.amount_return_count, 0)}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-emerald-600 font-bold">{item.return_success_count}</span>
                          <span className="text-muted-foreground mx-1">/</span>
                          <span className="text-red-600 font-bold">{item.return_fail_count}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {getStatusBadge(item.status)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {item.user_created_id ? (
                            <span className="text-foreground">
                              {/* แสดง firstname lastname ถ้ามี ไม่งั้นแสดง username */}
                              {item.user_created_id.firstname || item.user_created_id.lastname ? (
                                `${item.user_created_id.firstname || ''} ${item.user_created_id.lastname || ''}`.trim()
                              ) : (
                                item.user_created_id.username || item.user_created_id.objectId?.slice(-6) || '-'
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDetail(item)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              ดู
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteTarget(item)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    แสดง {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} จาก {total.toLocaleString()} รายการ
                  </span>
                  <Select
                    value={String(limit)}
                    onValueChange={(v) => { setLimit(Number(v)); setPage(1) }}
                  >
                    <SelectTrigger className="w-[70px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(1)}
                    disabled={page === 1 || isLoading}
                  >
                    หน้าแรก
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1 || isLoading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm px-2 min-w-[100px] text-center">
                    หน้า {page} / {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || isLoading}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages || isLoading}
                  >
                    หน้าสุดท้าย
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>


      {/* Create Dialog */}
      <CreateReturnLoseDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Detail Dialog */}
      <ReturnLoseDetailDialog
        returnLose={selectedReturnLose}
        open={showDetailDialog}
        onClose={() => { setShowDetailDialog(false); setSelectedReturnLose(null) }}
        onProcess={handleProcessSuccess}
      />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              ยืนยันการลบ
            </AlertDialogTitle>
            <AlertDialogDescription>
              คุณต้องการลบรายการคืนยอดเสียนี้หรือไม่?
              <div className="mt-3 p-3 bg-muted rounded-lg text-sm space-y-1">
                <p>UUID: <strong className="text-foreground">{deleteTarget?.uuid}</strong></p>
                <p>วันที่: <strong className="text-foreground">{deleteTarget?.date_return}</strong></p>
                <p>กลุ่มเกม: <strong className="text-foreground">{deleteTarget?.game_group?.join(', ')}</strong></p>
                <p>สมาชิก: <strong className="text-foreground">{deleteTarget?.member_count} ราย</strong></p>
                <p>ยอดคืน: <strong className="text-pink-600">฿{formatNumber(deleteTarget?.amount_return_count || 0, 0)}</strong></p>
              </div>
              <p className="mt-3 text-destructive font-medium">⚠️ การลบนี้จะซ่อนรายการออกจากระบบ</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleDelete()
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              ยืนยันลบ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
